(function () {
  const SELECTOR = '[data-capa-wine-bar-settings]';

  // Capa's dated API. Every read goes through the CDN with the section's
  // existing published key. The old /v2/api and /v3/api paths are retired.
  const CAPA_BASE_URL = 'https://cdn.capacms.com';
  const CAPA_VERSION = '2026-10-01';
  const CAPA_HOSTS = ['cdn.capacms.com', 'api.capacms.com'];

  function fieldValue(value) {
    if (Array.isArray(value)) return fieldValue(value[0]);

    if (value && typeof value === 'object') {
      if (value.value !== undefined) return fieldValue(value.value);

      return (
        value.url ||
        value.fileUrl ||
        value.file_url ||
        value.publicUrl ||
        value.public_url ||
        value.preview_url ||
        value.src ||
        value.path ||
        ''
      );
    }

    return value;
  }

  function normalizeResponse(payload) {
    if (!payload) return null;

    const container = payload.data && !Array.isArray(payload.data) ? payload.data : payload;
    const instances =
      payload.modelInstances ||
      container.modelInstances ||
      (Array.isArray(payload.data) ? payload.data : null) ||
      (Array.isArray(container.data) ? container.data : null) ||
      payload.instances ||
      container.instances;

    const instance = Array.isArray(instances) ? instances[0] : container;
    if (!instance) return null;

    // /api/ entries carry their values in `fields`, already flat (files are { url, ... }).
    const rawData =
      (instance.fields && typeof instance.fields === 'object' ? instance.fields : null) ||
      instance.data ||
      instance.currentVersion?.data ||
      instance.currentVersion?.schema?.data ||
      instance;
    if (!rawData) return null;

    if (Array.isArray(rawData)) {
      return rawData.reduce((acc, field) => {
        const key = field.namespace || field.key || field.name || field.id;
        if (!key) return acc;
        acc[key] = fieldValue(field.value ?? field.data ?? field.text ?? '');
        return acc;
      }, {});
    }

    return Object.keys(rawData).reduce((acc, key) => {
      const value = rawData[key];
      acc[key] = fieldValue(value && typeof value === 'object' && !Array.isArray(value)
        ? value.value ?? value.data ?? value.text ?? value.url ?? value
        : value);
      return acc;
    }, {});
  }

  function entriesUrl(namespace, query) {
    const params = new URLSearchParams(query || '');
    if (!params.has('limit')) params.set('limit', '1');
    if (!params.has('select')) params.set('select', '*');
    return `${CAPA_BASE_URL}/api/entries/${encodeURIComponent(namespace)}?${params.toString()}`;
  }

  // A source URL saved in the theme editor may still name a retired path,
  // such as https://cdn.capacms.com/v2/api/tolo_homepage?limit=1. Read the
  // same model through /api/entries instead. Non-Capa URLs pass through.
  function datedSourceUrl(raw) {
    let url;
    try {
      url = new URL(raw, window.location.href);
    } catch (error) {
      return raw;
    }
    if (!CAPA_HOSTS.includes(url.hostname)) return raw;

    const legacy = url.pathname.match(/^\/v[23]\/api\/([^/]+)\/?$/);
    if (legacy) return entriesUrl(decodeURIComponent(legacy[1]), url.search);

    const dated = url.pathname.match(/^\/api\/entries\/([^/]+)\/?$/);
    if (dated) return entriesUrl(decodeURIComponent(dated[1]), url.search);

    return raw;
  }

  function sourceUrl(root) {
    if (root.dataset.capaSourceUrl) return datedSourceUrl(root.dataset.capaSourceUrl);
    if (!root.dataset.capaModelId) return '';

    const namespace = root.dataset.capaModelNamespace || 'wine_bar_settings';
    return entriesUrl(namespace);
  }

  function setText(root, selector, value) {
    if (!value) return;
    const el = root.querySelector(selector);
    if (el) el.textContent = value;
  }

  function unwrapSingleParagraphHtml(value) {
    if (typeof value !== 'string') return value;

    const template = document.createElement('template');
    template.innerHTML = value.trim();

    if (template.content.children.length !== 1) return value;

    const onlyChild = template.content.firstElementChild;
    if (!onlyChild || onlyChild.tagName !== 'P') return value;

    return onlyChild.innerHTML;
  }

  function findHoursTarget(root) {
    const explicitTarget = root.querySelector('[data-capa-field="hours"]');
    if (explicitTarget) return explicitTarget;

    return Array.from(root.querySelectorAll('.prose p')).find((paragraph) =>
      /wine\s+bar\s+hours/i.test(paragraph.textContent || '')
    );
  }

  function setHtml(root, selector, value) {
    if (!value) return;

    const el = selector === '[data-capa-field="hours"]'
      ? findHoursTarget(root)
      : root.querySelector(selector);
    if (!el) return;

    el.innerHTML = el.tagName === 'P' ? unwrapSingleParagraphHtml(value) : value;
  }

  function setHref(root, selector, value) {
    if (!value) return;
    const el = root.querySelector(selector);
    if (el) el.setAttribute('href', value);
  }

  function applySettings(root, data) {
    setText(root, '[data-capa-field="title"]', data.title);
    setHtml(root, '[data-capa-field="hours"]', data.hours);

    root.querySelectorAll('[data-capa-link]').forEach((link) => {
      const field = link.dataset.capaLink;
      if (!field) return;
      setHref(root, `[data-capa-link="${field}"]`, data[field]);
    });
  }

  async function hydrate(root) {
    const hasDirectSource = Boolean(root.dataset.capaSourceUrl);
    const url = sourceUrl(root);
    if (!url) return;
    if (!hasDirectSource && !root.dataset.capaApiKey) return;

    const headers = { Accept: 'application/json' };
    if (url.startsWith(`${CAPA_BASE_URL}/api/`)) headers['Capa-Version'] = CAPA_VERSION;
    if (root.dataset.capaApiKey) {
      if (root.dataset.capaApiKey.toLowerCase().startsWith('bearer ')) {
        headers.Authorization = root.dataset.capaApiKey;
      } else {
        headers['x-api-key'] = root.dataset.capaApiKey;
      }
    }

    try {
      const response = await fetch(url, { headers, credentials: 'omit' });
      if (!response.ok) throw new Error(`Capa request failed: ${response.status}`);
      const data = normalizeResponse(await response.json());
      if (data) applySettings(root, data);
    } catch (error) {
      // Keep Shopify section settings as safe fallbacks if Capa is unavailable or not configured.
      console.warn('[Parcelle] Capa wine bar settings unavailable', error);
    }
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach(hydrate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
